import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import Correction from '../../components/correction';
import FluidContainer from '../layouts/components/fluid-container';
import { Show } from '../show';
import SharedText from '../../components/shared-text';
import AudioPlayer from '../../components/audio-player';
import GridContainer from '../layouts/components/grid-container';
import ImageGallery from '../../components/image-gallery';
import { get } from 'lodash';
import YouTubePlayer from '../../components/video-player/youtube';
import MathContent from '../../components/math-content';
import { CORRECT, WRONG } from '../../lib/exercise';
import { S3_IMAGES_URL } from '../../config';
import Highlighter from '../../components/highlither';
import { Body } from '../typography';
import Stack from '../stack';

const ResultContainer = styled(FluidContainer)`
    margin: 16px 52px 24px 16px;
`;

export const GenericCorrection = styled(FluidContainer)`
    flex-direction: column;
    font-size: 16px;
    letter-spacing: 0;
    line-height: 24px;
    border-bottom: 1px solid var(--gray700);
    padding: 0 16px 16px 16px;

    img {
        margin: 4px;
        vertical-align: middle;
    }
`;

export const TreatmentContainer = styled.div`
    padding: 0 48px 24px 48px;

    & > div {
        margin-top: 8px;
    }

    img {
        display: inline-block;
        max-width: 100%;
    }
`;

const ScrollableContent = styled.div`
    max-width: 100%;
    overflow-x: auto;
`;

const GenericFeedback = ({ label = '', feedback }) => {
    return feedback ? <MathContent>{`${label} ${feedback}`}</MathContent> : '';
};

GenericFeedback.propTypes = {
    label: PropTypes.string,
    feedback: PropTypes.string,
};

const GenericCorrectionFeedback = ({ solution, exercise, correctionStatus }) => {
    if (
        !(
            get(exercise, 'feedbacks.genericFeedback') ||
            (get(exercise, 'feedbacks.genericCorrectFeedback') && correctionStatus && correctionStatus.state === CORRECT) ||
            (get(exercise, 'feedbacks.genericErrorFeedback') && correctionStatus && correctionStatus.state === WRONG)
        )
    ) {
        return <></>;
    }

    return solution ? (
        <GenericCorrection paddingBottom={16} flexDirection="column">
            <GenericFeedback label="Feedback generico sull'esercizio: " feedback={get(exercise, 'feedbacks.genericFeedback')} />
            <GenericFeedback label="Feedback dell'esercizio corretto: " feedback={get(exercise, 'feedbacks.genericCorrectFeedback')} />
            <GenericFeedback label="Feedback dell'esercizio sbagliato: " feedback={get(exercise, 'feedbacks.genericErrorFeedback')} />
        </GenericCorrection>
    ) : (
        <GenericCorrection paddingBottom={16} flexDirection="column">
            <GenericFeedback feedback={get(exercise, 'feedbacks.genericFeedback')} />
            <GenericFeedback
                feedback={
                    correctionStatus && correctionStatus.state === CORRECT
                        ? get(exercise, 'feedbacks.genericCorrectFeedback')
                        : get(exercise, 'feedbacks.genericErrorFeedback')
                }
            />
        </GenericCorrection>
    );
};

GenericCorrectionFeedback.propTypes = {
    exercise: PropTypes.object,
    genericFeedback: PropTypes.string, // TODO: Controllre se da usare
    solution: PropTypes.bool,
    correctionStatus: PropTypes.object,
};

export const CorrectionContent = ({ answer, correctionStatus, exercise, geogebraExercises, onPlayVideo, solution = false, videos }) => {
    const hasSharedText = !!get(exercise, 'sharedText');
    const hasImagesAttachments = get(exercise, 'attachments.images') && exercise.attachments?.images?.length > 0;

    const preview = get(exercise, 'attachments.geogebra.previewUrl');
    const defaultImage = `${S3_IMAGES_URL}/${preview}`;

    const ggbbase64 = geogebraExercises.find((item) => item.id === exercise.id);
    const updatedImage = `data:image/png;base64, ${get(ggbbase64, 'image')}`;

    return (
        <>
            <GenericCorrectionFeedback solution={solution} exercise={exercise} correctionStatus={correctionStatus} />
            <FluidContainer>
                <ResultContainer>
                    <FluidContainer flexDirection="column">
                        <Show when={hasSharedText}>
                            <FluidContainer paddingBottom={24} paddingLeft={32} flexWrap="wrap">
                                <SharedText exercise={exercise} showExpandButton={true} />
                            </FluidContainer>
                        </Show>
                        {exercise.attachments?.audio && (
                            <FluidContainer paddingBottom={24} paddingLeft={32}>
                                <AudioPlayer source={exercise.attachments.audio?.src} />
                            </FluidContainer>
                        )}

                        <GridContainer
                            className="ck-content"
                            columns={[1, exercise.attachments?.video || hasImagesAttachments || exercise.attachments?.geogebra ? 2 : 1]}
                        >
                            <Show when={exercise.attachments?.video || hasImagesAttachments || exercise.attachments?.geogebra}>
                                <div style={{ minWidth: 0, paddingLeft: '32px' }}>
                                    <Show when={exercise.attachments?.geogebra}>
                                        <div style={{ minWidth: 0, paddingBottom: '24px' }}>
                                            <ImageGallery
                                                images={[
                                                    {
                                                        alt: 'geogebra',
                                                        src: defaultImage,
                                                        caption: 'Figura originale',
                                                    },
                                                ]}
                                            />
                                        </div>
                                        {get(ggbbase64, 'image') && (
                                            <div style={{ minWidth: 0, paddingBottom: '24px' }}>
                                                <ImageGallery
                                                    images={[
                                                        {
                                                            alt: 'geogebra',
                                                            src: updatedImage,
                                                            caption: 'Figura rielaborata',
                                                        },
                                                    ]}
                                                />
                                            </div>
                                        )}
                                    </Show>

                                    <Stack gap="desktop">
                                        {exercise.attachments?.video && (
                                            <YouTubePlayer
                                                videoIdOrSource={exercise.attachments.video?.src}
                                                onPlayVideo={() => onPlayVideo(exercise.id)}
                                                showPreview={!videos.find((item) => item.exerciseId === exercise.id)}
                                            />
                                        )}
                                        {hasImagesAttachments && (
                                            <div style={{ minWidth: 0, paddingBottom: '24px' }}>
                                                <ImageGallery images={get(exercise, 'attachments.images')} />
                                            </div>
                                        )}
                                    </Stack>
                                </div>
                            </Show>
                            <Correction answers={answer} exercise={exercise} solution={solution} correctionStatus={correctionStatus} />
                        </GridContainer>
                    </FluidContainer>
                </ResultContainer>
            </FluidContainer>
            {exercise.treatment?.length > 0 && (
                <TreatmentContainer>
                    <Body level={3} fontWeight="semi-bold">
                        {'Svolgimento'}
                    </Body>
                    {exercise.treatment.map((item, index) => {
                        return (
                            <Highlighter key={index}>
                                <ScrollableContent>
                                    <MathContent>{item.text}</MathContent>
                                </ScrollableContent>
                            </Highlighter>
                        );
                    })}
                </TreatmentContainer>
            )}
        </>
    );
};

CorrectionContent.propTypes = {
    answer: PropTypes.object,
    correctionStatus: PropTypes.object,
    exercise: PropTypes.object,
    geogebraExercises: PropTypes.array,
    onPlayVideo: PropTypes.func,
    solution: PropTypes.bool,
    videos: PropTypes.array,
};

export default CorrectionContent;
